import { useState } from 'react';
import { Bell, Moon, Sun, LogOut, User, Loader2, Menu, Check, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useLogout } from '@/hooks/useAuth';
import { useUiStore } from '@/stores/ui.store';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  useUnreadNotificationCount,
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/hooks/useNotifications';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { roleLabel } from '@/lib/roles';
import { BrandMark } from '@/components/brand/BrandMark';
import { APP_NAME } from '@/lib/brand';
import { SHORTCUT_LABEL } from '@/lib/platform';

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: unreadData } = useUnreadNotificationCount();
  const { data: notifications, isLoading } = useNotifications(open);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unreadCount = unreadData?.count ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="tour-notifications"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="bg-destructive text-destructive-foreground absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-0.5 text-xs"
              onClick={() => void markAllRead.mutateAsync()}
              disabled={markAllRead.isPending}
            >
              Mark all read
            </Button>
          )}
        </div>

        <ScrollArea className="h-80">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : !notifications?.length ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <Bell className="text-muted-foreground h-8 w-8" />
              <p className="text-muted-foreground text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    'hover:bg-muted/50 flex cursor-pointer gap-3 px-4 py-3 transition-colors',
                    !n.isRead && 'bg-primary/5',
                  )}
                  onClick={() => {
                    if (!n.isRead) void markRead.mutateAsync(n.id);
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm', !n.isRead && 'font-medium')}>{n.title}</p>
                    <p className="text-muted-foreground line-clamp-2 text-xs">{n.body}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  {!n.isRead && <div className="bg-primary mt-2 h-2 w-2 shrink-0 rounded-full" />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // Ends this session on the server too (other devices stay signed in)
  const logoutMutation = useLogout();
  const { theme, setTheme, toggleSidebar } = useUiStore();
  const brandName = user?.orgName ?? APP_NAME;

  function handleLogout() {
    logoutMutation.mutate();
  }

  const initials = user
    ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase()
    : '??';

  return (
    <header className="bg-card flex h-16 shrink-0 items-center border-b px-4 md:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="mr-2 md:hidden"
        onClick={toggleSidebar}
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Compact brand — only below md, where the sidebar (and its brand header) is off-canvas */}
      <Link
        to="/dashboard"
        className="mr-3 flex min-w-0 items-center gap-2 md:hidden"
        aria-label={`${brandName} home`}
      >
        {user?.orgLogoUrl ? (
          <img
            src={user.orgLogoUrl}
            alt=""
            className="h-7 w-7 shrink-0 rounded-lg object-contain"
          />
        ) : (
          <BrandMark className="h-7 w-7" />
        )}
        <span className="font-display text-foreground truncate text-base font-bold">
          {brandName}
        </span>
      </Link>

      <div className="flex flex-1 items-center justify-end gap-3">
        {/* CMD+K trigger — visible on md+ screens */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              id="tour-search"
              onClick={() => {
                document.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }),
                );
              }}
              className="border-input bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground hidden w-64 items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors md:flex"
              aria-label="Open command palette"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="bg-background rounded border px-1.5 py-0.5 font-mono text-[10px]">
                {SHORTCUT_LABEL}
              </kbd>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="flex items-center gap-1">
            <kbd className="bg-muted rounded border px-1.5 py-0.5 font-mono text-[10px]">
              {SHORTCUT_LABEL}
            </kbd>
          </TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Show opposite icon: Sun in dark mode (click → light), Moon in light mode (click → dark) */}
            <Button
              variant="ghost"
              size="icon"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            <DropdownMenuItem
              onClick={() => {
                setTheme('light');
              }}
              className={theme === 'light' ? 'bg-accent font-medium' : ''}
            >
              <Sun className="h-4 w-4" /> Light
              {theme === 'light' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setTheme('dark');
              }}
              className={theme === 'dark' ? 'bg-accent font-medium' : ''}
            >
              <Moon className="h-4 w-4" /> Dark
              {theme === 'dark' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatarUrl ?? undefined} alt={user?.firstName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-muted-foreground text-xs">
                  {user?.workEmail ?? user?.employeeCode}
                </p>
                <p className="text-primary text-xs font-medium">{roleLabel(user?.role)}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                void navigate('/settings');
              }}
            >
              <User className="h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
